/**
 * QR-detectie: `BarcodeDetector` waar hij bestaat (Android/Chrome), anders zxing-wasm
 * (iOS) — D95. De payload is letterlijk het `ParcelNumber` (D41), dus er wordt niets
 * opgezocht: matchen gebeurt lokaal tegen de gecachte rit en werkt dus offline.
 */

export interface CodeDetector {
  readonly engine: 'native' | 'wasm';
  detect(video: HTMLVideoElement): Promise<string[]>;
  dispose(): void;
}

function hasNativeDetector(): boolean {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector !== 'undefined';
}

class NativeDetector implements CodeDetector {
  readonly engine = 'native' as const;
  private readonly detector = new BarcodeDetector({ formats: ['qr_code'] });

  async detect(video: HTMLVideoElement): Promise<string[]> {
    const results = await this.detector.detect(video);
    return results.map((result) => result.rawValue).filter((value) => value.length > 0);
  }

  dispose(): void {
    /* niets vast te houden */
  }
}

type ReadBarcodes = (
  image: ImageData,
  options: { formats: string[]; maxNumberOfSymbols: number },
) => Promise<{ text: string }[]>;

class WasmDetector implements CodeDetector {
  readonly engine = 'wasm' as const;
  private canvas: HTMLCanvasElement | null = null;

  constructor(private readonly read: ReadBarcodes) {}

  async detect(video: HTMLVideoElement): Promise<string[]> {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) return [];

    this.canvas ??= document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    const context = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return [];
    context.drawImage(video, 0, 0, width, height);
    const image = context.getImageData(0, 0, width, height);
    const results = await this.read(image, { formats: ['QRCode'], maxNumberOfSymbols: 1 });
    return results.map((result) => result.text).filter((value) => value.length > 0);
  }

  dispose(): void {
    this.canvas = null;
  }
}

/** Laadt de WASM-terugval pas als hij nodig is; de bundel blijft daardoor klein. */
async function createWasmDetector(): Promise<CodeDetector> {
  const [{ prepareZXingModule, readBarcodes }, wasmModule] = await Promise.all([
    import('zxing-wasm/reader'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);
  const wasmUrl = wasmModule.default;
  prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? wasmUrl : `${prefix}${path}`,
    },
  });
  return new WasmDetector(readBarcodes as unknown as ReadBarcodes);
}

export async function createDetector(): Promise<CodeDetector> {
  if (hasNativeDetector()) return new NativeDetector();
  return createWasmDetector();
}

/**
 * Dezelfde code niet vaker dan één keer per 2 s verwerken — anders vuurt een label dat in
 * beeld blijft liggen tientallen handelingen af.
 */
export class ScanDedupe {
  private readonly seen = new Map<string, number>();

  constructor(
    private readonly windowMs = 2_000,
    private readonly now: () => number = () => Date.now(),
  ) {}

  accept(code: string): boolean {
    const at = this.now();
    const last = this.seen.get(code);
    if (last !== undefined && at - last < this.windowMs) return false;
    this.seen.set(code, at);
    for (const [key, seenAt] of this.seen) {
      if (at - seenAt > this.windowMs * 5) this.seen.delete(key);
    }
    return true;
  }

  reset(): void {
    this.seen.clear();
  }
}
